import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Table, Pagination, Icon, Tab } from 'semantic-ui-react';
import AlertDataModalContainer from '../../containers/AlertDataModalContainer';

import { ArrowDown, ArrowUp } from '../../assets/icons';

import styles from './Alerts.module.scss';

const Alerts = React.memo(({ alertsOccur, alertsRes, onFetchAlertData }) => {
  const [t] = useTranslation();

  const [currentAlertsOccurPage, setAlertsOccurPage] = useState(1);
  const [currentAlertsResPage, setAlertsResPage] = useState(1);
  const [dataAlertsOccur, setDataAlertsOccur] = useState([]);
  const [dataAlertsRes, setDataAlertsRes] = useState([]);
  const [openDataModal, setOpenDataModal] = useState(false);
  const [currentAlert, setCurrentAlert] = useState({});

  const resultsPerPage = 15;
  const totalAlertsOccurPages = Math.ceil(alertsOccur.length / resultsPerPage);
  const totalAlertsResPages = Math.ceil(alertsRes.length / resultsPerPage);
  const dateTimeShowFormat = 'dd/MM/yyyy HH:mm:ss';
  const dateShowFormat = 'dd/MM/yyyy';
  const timeShowFormat = 'HH:mm:ss';

  const onPageAlertsOccurChange = (e, { activePage }) => {
    setAlertsOccurPage(activePage);
  };

  const onPageAlertsResChange = (e, { activePage }) => {
    setAlertsResPage(activePage);
  };

  useEffect(() => {
    setDataAlertsOccur(
      alertsOccur.slice(
        (currentAlertsOccurPage - 1) * resultsPerPage,
        currentAlertsOccurPage * resultsPerPage,
      ),
    );
    setDataAlertsRes(
      alertsRes.slice(
        (currentAlertsResPage - 1) * resultsPerPage,
        currentAlertsResPage * resultsPerPage,
      ),
    );
  }, [alertsOccur, alertsRes, currentAlertsOccurPage, currentAlertsResPage]);

  const alertStatus = (status) => {
    switch (status) {
      case -2:
        return (
          <div className="status_below_2">
            <ArrowDown />
            {t('common.alerts.belowThreshold')}
          </div>
        );
      case -1:
        return (
          <div className="status_below_1">
            <ArrowDown />
            {t('common.alerts.belowThreshold')}
          </div>
        );
      case 1:
        return (
          <div className="status_over_1">
            <ArrowUp />
            {t('common.alerts.overTheThreshold')}
          </div>
        );
      case 2:
        return (
          <div className="status_over_2">
            <ArrowUp />
            {t('common.alerts.overTheThreshold')}
          </div>
        );
      default:
        return (
          <div className="status_over_2">
            <span className="status_error_icon" />
            {t('common.alerts.error')}
          </div>
        );
    }
  };

  const handClickAlert = useCallback(
    (item) => {
      const data = {
        itemName: item.item?.itemName,
        occurTime: item.occurTime,
        alertId: item.id,
      };
      setOpenDataModal(true);
      setCurrentAlert(item.id);
      onFetchAlertData(item.deviceId, data);
    },
    [onFetchAlertData],
  );

  const listAlert = (alerts, type, currentPage, onPageChange, totalPages) => (
    <>
      <div className={styles.tableWrapper}>
        <Table singleLine>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className="uppercase text-xs text-grey">
                {type === 'resolved' ? t('common.alerts.occurDaytime') : t('common.alerts.day')}
              </Table.HeaderCell>
              <Table.HeaderCell className="uppercase text-xs text-grey">
                {type === 'resolved' ? t('common.alerts.resDaytime') : t('common.alerts.time')}
              </Table.HeaderCell>
              <Table.HeaderCell className="uppercase text-xs text-grey">
                {t('common.alerts.content')}
              </Table.HeaderCell>
              <Table.HeaderCell className="uppercase text-xs text-grey">
                {t('common.alerts.status')}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {alerts &&
              alerts.map((item) => {
                return (
                  <Table.Row
                    className={styles.alertItem}
                    key={item.id}
                    onClick={() => handClickAlert(item)}
                  >
                    <Table.Cell>
                      <span className={styles.item_update}>
                        {type === 'resolved'
                          ? format(new Date(item.occurTime), dateTimeShowFormat)
                          : format(new Date(item.occurTime), dateShowFormat)}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className={styles.item_update}>
                        {type === 'resolved'
                          ? format(new Date(item.resTime), dateTimeShowFormat)
                          : format(new Date(item.occurTime), timeShowFormat)}
                      </span>
                    </Table.Cell>

                    <Table.Cell>
                      <span
                        className={styles.itemName}
                      >{`${item.device?.name} - ${item.item?.name} - ${item.alertSetting?.name}`}</span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className={styles.item}>
                        {alertStatus(item.alertSetting?.alertLevel)}
                      </span>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
          </Table.Body>
        </Table>
        <AlertDataModalContainer
          open={openDataModal}
          onClose={() => setOpenDataModal(false)}
          id={currentAlert}
          type={type}
        />
      </div>
      <div className={`text-right ${styles.pagination_wrapper}`}>
        <Pagination
          activePage={currentPage}
          boundaryRange={0}
          onPageChange={onPageChange}
          size="mini"
          siblingRange={1}
          prevItem={{ content: <Icon name="angle left" />, icon: true }}
          nextItem={{ content: <Icon name="angle right" />, icon: true }}
          firstItem={{ content: <Icon name="angle double left" />, icon: true }}
          lastItem={{ content: <Icon name="angle double right" />, icon: true }}
          totalPages={totalPages}
        />
      </div>
    </>
  );

  const panes = [
    {
      menuItem: t('common.alerts.warning'),
      render: () => (
        <Tab.Pane>
          {listAlert(
            dataAlertsOccur,
            'warning',
            currentAlertsOccurPage,
            onPageAlertsOccurChange,
            totalAlertsOccurPages,
          )}
        </Tab.Pane>
      ),
    },
    {
      menuItem: t('common.alerts.resolved'),
      render: () => (
        <Tab.Pane>
          {listAlert(
            dataAlertsRes,
            'resolved',
            currentAlertsResPage,
            onPageAlertsResChange,
            totalAlertsResPages,
          )}
        </Tab.Pane>
      ),
    },
  ];

  return (
    <div className={styles.devicesWrapper}>
      <h2 className="page-title">{t('common.alert')}</h2>
      <Tab panes={panes} />
    </div>
  );
});

Alerts.propTypes = {
  alertsOccur: PropTypes.arrayOf(PropTypes.any).isRequired,
  alertsRes: PropTypes.arrayOf(PropTypes.any).isRequired,
  onFetchAlertData: PropTypes.func.isRequired,
};

export default Alerts;
